import { useEffect, useRef, useState } from "react";

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
}

export default function OtpInput({ length = 6, value, onChange, disabled = false, fullWidth = false }: OtpInputProps) {
  const inputs = Array.from({ length });
  const refs = useRef<HTMLInputElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    refs.current = refs.current.slice(0, length);
  }, [length]);

  const getChar = (index: number) => value[index] || "";

  const handleChange = (index: number, nextValue: string) => {
    const digitsOnly = nextValue.replace(/\D/g, "");
    const chars = Array.from({ length }, (_, i) => value[i] || "");

    if (digitsOnly.length > 1) {
      // Paste or autocomplete
      for (let i = 0; i < length && i < digitsOnly.length; i++) {
        chars[i] = digitsOnly[i];
      }
      onChange(chars.join(""));
      const nextFocus = Math.min(digitsOnly.length, length) - 1;
      refs.current[nextFocus]?.focus();
    } else {
      const digit = digitsOnly.slice(-1);
      chars[index] = digit;
      onChange(chars.join(""));
      if (digit && index < length - 1) refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      if (!getChar(index) && index > 0) {
        const chars = Array.from({ length }, (_, i) => value[i] || "");
        chars[index - 1] = "";
        onChange(chars.join(""));
        refs.current[index - 1]?.focus();
      } else if (getChar(index)) {
        const chars = Array.from({ length }, (_, i) => value[i] || "");
        chars[index] = "";
        onChange(chars.join(""));
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    const chars = Array.from({ length }, (_, i) => pasted[i] || "");
    onChange(chars.join(""));
    const nextFocus = Math.min(pasted.length, length) - 1;
    refs.current[nextFocus]?.focus();
  };

  const handleClick = (index: number) => {
    // Allow clicking any box — move to first empty or current box
    const firstEmpty = Array.from({ length }, (_, i) => i).find((i) => !getChar(i));
    const target = firstEmpty !== undefined ? Math.min(firstEmpty, index) : index;
    refs.current[target]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {inputs.map((_, index) => {
        const filled = !!getChar(index);
        const focused = focusedIndex === index;
        let borderColor = "#E5E7EB";
        if (focused) borderColor = "#00553E";
        else if (filled) borderColor = "#7ABBA3";

        return (
          <input
            key={index}
            ref={(el) => {
              if (el) refs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            value={getChar(index)}
            disabled={disabled}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(null)}
            onClick={() => handleClick(index)}
            className={`${fullWidth ? "flex-1 min-w-0" : "w-12"} h-14 rounded-[10px] border-2 text-center text-xl font-semibold text-[#0B1F16] bg-white transition-colors disabled:opacity-50 outline-none`}
            style={{ borderColor }}
          />
        );
      })}
    </div>
  );
}
